import React, { useState, useMemo, useEffect } from 'react';
import { Holding } from '../types';
import { useLanguage } from '../context/LanguageContext';
import { market } from '../services/api';

interface Props {
  holdings: Holding[];
  selectedSymbol?: string;
}

export const InvestmentForecaster: React.FC<Props> = ({ holdings, selectedSymbol: propSymbol }) => {
  const { t, language } = useLanguage();
  const [eurUsdRate, setEurUsdRate] = useState(1.09);

  // Fetch the live exchange rate on load
  useEffect(() => {
    market.getFxRate()
      .then(res => {
        if (res.data && res.data.rate) {
          setEurUsdRate(res.data.rate);
        }
      })
      .catch(err => {
        console.error('Failed to load live exchange rate in InvestmentForecaster', err);
      });
  }, []);

  // Available assets for forecasting in EUR baseline
  const availableAssets = useMemo(() => {
    const symbols = holdings.map(h => {
      const isEur = h.currency === 'EUR';
      const rate = isEur ? 1.0 : eurUsdRate;
      
      const rawPrice = h.current_price || h.avg_cost || 100;
      const priceInEur = rawPrice / rate;
      const holdingValueInEur = (h.quantity * rawPrice) / rate;

      return {
        symbol: h.symbol,
        price: priceInEur,
        holdingValue: holdingValueInEur,
        currency: 'EUR'
      };
    });
    
    // Add defaults if not present (converted to EUR base)
    if (!symbols.some(s => s.symbol === 'CSG')) symbols.push({ symbol: 'CSG', price: 148.50, holdingValue: 172881, currency: 'EUR' });
    if (!symbols.some(s => s.symbol === 'SPY')) symbols.push({ symbol: 'SPY', price: 540.20 / eurUsdRate, holdingValue: 10000 / eurUsdRate, currency: 'EUR' });
    if (!symbols.some(s => s.symbol === 'BTC')) symbols.push({ symbol: 'BTC', price: 64200.00 / eurUsdRate, holdingValue: 5000 / eurUsdRate, currency: 'EUR' });
    if (!symbols.some(s => s.symbol === 'GLD')) symbols.push({ symbol: 'GLD', price: 215.10 / eurUsdRate, holdingValue: 2500 / eurUsdRate, currency: 'EUR' });
    
    return symbols;
  }, [holdings, eurUsdRate]);

  const [selectedSymbol, setSelectedSymbol] = useState('CSG');
  
  // Sync selected symbol from dashboard
  useEffect(() => {
    if (propSymbol) {
      setSelectedSymbol(propSymbol);
    }
  }, [propSymbol]);

  const activeAsset = useMemo(() => {
    return availableAssets.find(a => a.symbol === selectedSymbol) || availableAssets[0];
  }, [availableAssets, selectedSymbol]);

  // Dynamic user principal state
  const [principalInput, setPrincipalInput] = useState<string>('');

  // Principal value used in calculations (already in EUR)
  const principal = useMemo(() => {
    if (principalInput === '') {
      return activeAsset ? activeAsset.holdingValue || 10000 : 10000;
    }
    return Math.max(0, Number(principalInput) || 0);
  }, [principalInput, activeAsset]);

  // Update input text if asset changes to match holding value
  useEffect(() => {
    if (activeAsset) {
      setPrincipalInput(String(Math.round(activeAsset.holdingValue)));
    }
  }, [selectedSymbol, activeAsset]);

  // Asset parameters (drift & volatility)
  const assetParams = useMemo(() => {
    const params: Record<string, { drift: number; vol: number }> = {
      'CSG': { drift: 0.14, vol: 0.22 },
      'SPY': { drift: 0.09, vol: 0.14 },
      'BTC': { drift: 0.28, vol: 0.48 },
      'GLD': { drift: 0.05, vol: 0.12 },
    };
    return params[selectedSymbol] || { drift: 0.10, vol: 0.20 };
  }, [selectedSymbol]);

  // Compute projections over specific horizons
  const projections = useMemo(() => {
    const mu = assetParams.drift;
    const sigma = assetParams.vol;
    const S0 = principal;

    const horizons = [
      { nameKey: 'horizon7d', days: 7, t: 7 / 365 },
      { nameKey: 'horizon14d', days: 14, t: 14 / 365 },
      { nameKey: 'horizon1m', days: 30, t: 30 / 365 },
      { nameKey: 'horizon6m', days: 180, t: 180 / 365 },
    ];

    return horizons.map(h => {
      // Expected ending value (drift formula)
      const expected = S0 * Math.exp(mu * h.t);
      const expectedReturn = ((expected - S0) / S0) * 100;

      // 10th and 90th percentile bounds based on standard normal distribution Z = 1.28
      const lowBound = S0 * Math.exp((mu - 0.5 * Math.pow(sigma, 2)) * h.t - 1.28 * sigma * Math.sqrt(h.t));
      const highBound = S0 * Math.exp((mu - 0.5 * Math.pow(sigma, 2)) * h.t + 1.28 * sigma * Math.sqrt(h.t));

      const lowReturn = ((lowBound - S0) / S0) * 100;
      const highReturn = ((highBound - S0) / S0) * 100;

      return {
        ...h,
        expected,
        expectedReturn,
        lowBound,
        lowReturn,
        highBound,
        highReturn
      };
    });
  }, [principal, assetParams, selectedSymbol]);

  // Unified Euro base currency symbol
  const currencySymbol = '€';

  return (
    <div className="glass-panel p-6 font-mono text-xs space-y-6">
      {/* Header section */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h3 className="text-sm font-extrabold uppercase tracking-widest text-[#1c1917]">
            {t("forecasterTitle")}
          </h3>
          <div className="text-[10px] text-stone-500 mt-1">{t("forecasterDesc")}</div>
        </div>

        {/* Inputs */}
        <div className="flex flex-wrap gap-3 w-full md:w-auto">
          <div className="flex-1 md:flex-none min-w-[120px]">
            <label htmlFor="forecaster-ticker" className="text-[9px] text-stone-500 block mb-1">{t("selectTickerLabel")}</label>
            <select
              id="forecaster-ticker"
              value={selectedSymbol}
              onChange={(e) => setSelectedSymbol(e.target.value)}
              className="w-full bg-[#eae6db] border border-[#1c1917] rounded px-2 py-1 font-bold text-[#1c1917] outline-none"
            >
              {availableAssets.map(a => (
                <option key={a.symbol} value={a.symbol}>{a.symbol} (EUR €{Number(a.price).toFixed(2)})</option>
              ))}
            </select>
          </div>

          <div className="flex-1 md:flex-none min-w-[140px]">
            <label htmlFor="forecaster-principal" className="text-[9px] text-stone-500 block mb-1">
              {t("principalLabel").replace('$', currencySymbol)}
            </label>
            <input
              id="forecaster-principal"
              type="number"
              value={principalInput}
              onChange={(e) => setPrincipalInput(e.target.value)}
              className="w-full bg-[#eae6db] border border-[#1c1917] rounded px-2 py-1 font-bold text-[#1c1917] outline-none focus:border-stone-600"
              placeholder="e.g. 10000"
            />
          </div>
        </div>
      </div>

      {/* Dynamic parameters readout */}
      <div className="flex justify-between items-center bg-[#f4f1ea] border border-[#1c1917]/10 rounded p-3 text-[10px] text-stone-600">
        <span>{t("annualDriftLabel")}: <strong className="text-[#1c1917]">{(assetParams.drift * 100).toFixed(0)}%</strong></span>
        <span>{t("annualVolLabel")}: <strong className="text-[#1c1917]">{(assetParams.vol * 100).toFixed(0)}%</strong></span>
      </div>

      {/* Projection grids */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        {projections.map((p, idx) => {
          const isPos = p.expectedReturn >= 0;
          return (
            <div key={idx} className="bg-[#f4f1ea] border border-[#1c1917] rounded p-4 flex flex-col justify-between h-40">
              <div>
                <div className="text-[10px] text-stone-500 font-extrabold uppercase tracking-widest border-b border-[#1c1917]/10 pb-1.5 mb-2.5">
                  {t(p.nameKey)}
                </div>
                
                {/* Expected end value */}
                <div className="text-base font-extrabold text-[#1c1917]">
                  {currencySymbol}{p.expected.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                </div>
                
                {/* Expected Return Percentage */}
                <div className={`text-[10px] font-bold mt-1 ${isPos ? 'text-green-700' : 'text-red-700'}`}>
                  {isPos ? '▲' : '▼'} {isPos ? '+' : ''}{p.expectedReturn.toFixed(2)}% {t("expectedReturnLabel")}
                </div>
              </div>

              {/* Confidence interval boundary labels */}
              <div className="space-y-1.5 pt-2 border-t border-[#1c1917]/10 text-[9px] text-stone-500">
                <div className="flex justify-between">
                  <span>{t("pctHighLabel")}</span>
                  <span className="font-extrabold text-[#1c1917]">{currencySymbol}{Math.round(p.highBound).toLocaleString()} (+{p.highReturn.toFixed(1)}%)</span>
                </div>
                <div className="flex justify-between">
                  <span>{t("pctLowLabel")}</span>
                  <span className="font-extrabold text-stone-700">{currencySymbol}{Math.round(p.lowBound).toLocaleString()} ({p.lowReturn.toFixed(1)}%)</span>
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};
